import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ArrowRight, Monitor, RefreshCw } from 'lucide-react';
import type { DeviceAccessStatus, DeviceRevocationResult } from '@zeroth/shared';
import { useAuth, useClerk } from '../../lib/clerk';
import { apiClient, getErrorMessage } from '../../lib/api';
import { apiErrorCode, DEVICE_ACCESS_EVENT, type DeviceAccessFailure } from '../../lib/deviceAccessEvents';
import { usePageTitle } from '../../components/layout/PageTitleProvider';
import { Button } from '../../components/ui/button';
import { ThemeToggle } from '../../components/ui/ThemeToggle';
import zerothLogo from '../../assets/zeroth-logo.png';

const touchTarget = 'min-h-[calc(48px*var(--app-density-scale-inverse))]';
const RECOVERY_POLL_MS = 3_000;
const ACTIVE_POLL_MS = 30_000;

export function DeviceAccessGate({ children }: { children: ReactNode }) {
  const { userId, sessionId } = useAuth();
  // Remount on identity changes: never reuse another session's admission.
  return <SessionDeviceGate key={`${userId}:${sessionId}`} sessionId={sessionId ?? undefined}>{children}</SessionDeviceGate>;
}

function SessionDeviceGate({ children, sessionId }: { children: ReactNode; sessionId?: string }) {
  const { signOut } = useClerk();
  const [status, setStatus] = useState<DeviceAccessStatus | null>(null);
  const [review, setReview] = useState(false);
  const [verificationError, setVerificationError] = useState(false);
  const [signedOut, setSignedOut] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<{ deviceId?: string; message: string } | null>(null);
  const [pendingLogout, setPendingLogout] = useState<string | null>(null);
  const [recoveryError, setRecoveryError] = useState(false);
  const latestStatus = useRef<DeviceAccessStatus | null>(null);
  const requestVersion = useRef(0);
  const readsInFlight = useRef(0);
  const retryNotBefore = useRef(0);
  const actionInProgress = useRef(false);
  const mounted = useRef(true);

  const applyStatus = useCallback((next: DeviceAccessStatus) => {
    latestStatus.current = next;
    setStatus(next);
    setVerificationError(false);
    setSignedOut(false);
    setRecoveryError(false);
    setPendingLogout(current => next.devices.some(device => device.deviceId === current) ? current : null);
  }, []);

  const refresh = useCallback(async (fresh = false) => {
    if (Date.now() < retryNotBefore.current) return null;
    const version = ++requestVersion.current;
    readsInFlight.current += 1;
    try {
      const response = await apiClient.get<{ data: DeviceAccessStatus }>(`/api/auth/devices${fresh ? '?refresh=true' : ''}`);
      if (!mounted.current || requestVersion.current !== version) return null;
      applyStatus(response.data);
      return response.data;
    } catch (error) {
      if (mounted.current && requestVersion.current === version) {
        const ended = apiErrorCode(error) === 'UNAUTHORIZED';
        const response = (error as { response?: { status?: number; headers?: Record<string, unknown> } } | null)?.response;
        if (response?.status === 429) {
          const seconds = Number(response.headers?.['retry-after']);
          retryNotBefore.current = Date.now() + (Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : ACTIVE_POLL_MS);
        }
        if (latestStatus.current?.allowed === false && !ended) {
          // Keep the recovery context visible while automatically retrying.
          setRecoveryError(true);
        } else {
          setVerificationError(true);
        }
        setSignedOut(ended);
      }
      return null;
    } finally {
      readsInFlight.current -= 1;
    }
  }, [applyStatus]);

  useEffect(() => {
    mounted.current = true;
    void refresh();
    const onFailure = (event: Event) => {
      requestVersion.current += 1;
      const code = (event as CustomEvent<DeviceAccessFailure>).detail;
      if (code === 'DEVICE_LIMIT_EXCEEDED') {
        if (latestStatus.current) latestStatus.current = { ...latestStatus.current, allowed: false };
        setStatus(latestStatus.current);
      } else {
        setVerificationError(true);
        setSignedOut(code === 'UNAUTHORIZED');
      }
      // Do not recursively retry here: the verification request itself can fail.
    };
    window.addEventListener(DEVICE_ACCESS_EVENT, onFailure);
    return () => {
      mounted.current = false;
      requestVersion.current += 1;
      window.removeEventListener(DEVICE_ACCESS_EVENT, onFailure);
    };
  }, [refresh]);

  const pollMs = status?.allowed === false && !signedOut ? RECOVERY_POLL_MS : ACTIVE_POLL_MS;
  useEffect(() => {
    const check = () => {
      if (document.visibilityState !== 'hidden' && !actionInProgress.current && readsInFlight.current === 0 && !signedOut) void refresh();
    };
    const timer = window.setInterval(check, pollMs);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, [pollMs, refresh, signedOut]);

  const showingSplash = !status || !status.allowed || verificationError;
  usePageTitle('Device limit · Zeroth', showingSplash);

  async function leave(redirectUrl: '/sign-in' | '/sign-up') {
    actionInProgress.current = true;
    requestVersion.current += 1;
    setBusy('signout'); setActionError(null);
    try {
      await signOut({ sessionId, redirectUrl });
    } catch (error) {
      setActionError({ message: getErrorMessage(error) });
    } finally { actionInProgress.current = false; setBusy(null); }
  }

  async function revoke(deviceId: string) {
    actionInProgress.current = true;
    requestVersion.current += 1; // An older poll must not overwrite a mutation result.
    setBusy(deviceId); setActionError(null);
    try {
      const response = await apiClient.post<{ data: DeviceRevocationResult }>(`/api/auth/devices/${encodeURIComponent(deviceId)}/revoke`);
      if (!mounted.current) return;
      if (response.data.currentSessionRevoked) {
        setSignedOut(true); setVerificationError(true);
        await signOut({ sessionId, redirectUrl: '/sign-in' });
      } else if (response.data.status) {
        setPendingLogout(deviceId);
        applyStatus(response.data.status);
      }
    } catch (error) {
      if (mounted.current) {
        setPendingLogout(null);
        setStatus(current => current ? { ...current, allowed: false } : null);
        setActionError({ deviceId, message: getErrorMessage(error) });
      }
    } finally { actionInProgress.current = false; if (mounted.current) setBusy(null); }
  }

  if (!showingSplash) return <>{children}</>;

  const loggingOut = busy && busy !== 'signout' ? busy : pendingLogout;
  const loggingOutLabel = status?.devices.find(device => device.deviceId === loggingOut)?.label;

  return (
    <div className="min-h-[var(--app-viewport-height)] bg-muted/50 text-foreground">
      <div className="absolute right-4 top-4 [&>button]:min-h-[calc(48px*var(--app-density-scale-inverse))] [&>button]:min-w-[calc(48px*var(--app-density-scale-inverse))]"><ThemeToggle /></div>
      <main className="flex min-h-[var(--app-viewport-height)] items-center justify-center px-4 py-20">
        <section aria-labelledby="device-heading" aria-busy={!!busy} className={`w-full ${review ? 'max-w-2xl' : 'max-w-lg'} rounded-xl border border-border bg-card px-6 py-9 text-center shadow-lg sm:px-9 sm:py-11`}>
          <div className="mb-9 flex items-center justify-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center overflow-hidden"><img src={zerothLogo} alt="" className="h-20 w-20 max-w-none shrink-0 object-contain" /></div>
            <span className="text-xl font-semibold tracking-tight">Zeroth</span>
          </div>
          {verificationError ? (
            <>
              <h1 id="device-heading" className="text-2xl font-semibold tracking-tight">{signedOut ? 'This session has ended' : 'Unable to verify devices'}</h1>
              <p className="mt-3 text-sm text-muted-foreground">{signedOut ? 'Sign in again to continue to Zeroth.' : 'We could not check your signed-in browsers. Please try again.'}</p>
              <Button className={`mt-7 ${touchTarget}`} disabled={!!busy} onClick={() => signedOut ? void leave('/sign-in') : void refresh(true)}>{signedOut ? 'Sign in again' : 'Try again'}</Button>
            </>
          ) : !status ? (
            <div role="status"><RefreshCw aria-hidden="true" className="mx-auto mb-4 h-6 w-6 animate-spin text-muted-foreground" /><h1 id="device-heading" className="text-lg font-medium">Checking your devices…</h1></div>
          ) : !review ? (
            <>
              <h1 id="device-heading" className="text-2xl font-semibold tracking-tight">Start your own Zeroth account today</h1>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">This login allows two signed-in browsers. Create your own account, or review your devices to continue.</p>
              <div className="mt-7 flex flex-col items-center gap-1">
                <Button className={touchTarget} disabled={!!busy} onClick={() => void leave('/sign-up')}>Create an account<ArrowRight aria-hidden="true" className="h-4 w-4" /></Button>
                <Button variant="ghost" className={`${touchTarget} text-muted-foreground`} disabled={!!busy} onClick={() => void leave('/sign-in')}>Log out of this device</Button>
              </div>
              <div className="mt-9 border-t border-border pt-7">
                <h2 className="text-sm font-semibold">Is this your account?</h2>
                <p className="mt-2 text-sm text-muted-foreground">Review your devices and choose which browsers stay signed in.</p>
                <Button variant="outline" className={`mt-4 ${touchTarget}`} disabled={!!busy} onClick={() => { setReview(true); void refresh(true); }}>Review devices</Button>
              </div>
            </>
          ) : (
            <>
              <h1 id="device-heading" className="text-2xl font-semibold tracking-tight">Too many devices using this account</h1>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">This login allows two signed-in browsers. Log out of another device below to make room for this browser.</p>
              <ul aria-label="Signed-in devices" className="mt-7 divide-y divide-border border-y border-border text-left">
                {status.devices.map(device => (
                  <li key={device.deviceId} className="py-4">
                    <div className="grid grid-cols-[1fr_auto] items-center gap-3 sm:grid-cols-[1.2fr_1fr_auto]">
                      <div className="flex items-start gap-2.5 min-w-0"><Monitor aria-hidden="true" className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" /><div className="min-w-0">
                        {device.isCurrent && <p className="text-sm font-semibold text-primary">This device</p>}
                        <p className="break-words text-sm font-medium">{device.label}</p>
                        {!device.allowed && <p className="mt-1 text-xs text-muted-foreground">Awaiting access</p>}
                      </div></div>
                      <div className="col-span-2 col-start-1 row-start-2 pl-6 sm:col-span-1 sm:col-start-auto sm:row-start-auto sm:pl-0">
                        <p className="text-xs leading-relaxed text-muted-foreground">{device.location ?? 'Location unavailable'}</p>
                        <p className="mt-1 text-xs text-muted-foreground">Last seen: {device.lastSeenAt ? new Date(device.lastSeenAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : 'Unavailable'}</p>
                      </div>
                      <Button variant="outline" className={`col-start-2 row-start-1 ${touchTarget} sm:col-start-auto sm:row-start-auto`} disabled={!!busy || pendingLogout === device.deviceId} isLoading={loggingOut === device.deviceId} onClick={() => void revoke(device.deviceId)}>{loggingOut === device.deviceId ? 'Logging out…' : 'Log out'}</Button>
                    </div>
                    {actionError?.deviceId === device.deviceId && <p role="alert" className="mt-2 text-sm text-destructive">{actionError.message}</p>}
                  </li>
                ))}
              </ul>
            </>
          )}
          {status && !status.allowed && !verificationError && (
            <div role="status" aria-live="polite" className="mt-6 space-y-2 text-sm">
              <div className="flex items-center justify-center gap-2"><RefreshCw aria-hidden="true" className="h-4 w-4 shrink-0 animate-spin text-primary" /><span>{recoveryError ? 'Connection interrupted. Retrying automatically…' : loggingOutLabel ? `Logging out of ${loggingOutLabel}…` : 'Waiting for an open browser spot…'}</span></div>
              <p className="text-muted-foreground">You’ll continue automatically as soon as this browser has access.</p>
            </div>
          )}
          {actionError && !actionError.deviceId && <p role="alert" className="mt-4 text-sm text-destructive">{actionError.message}</p>}
        </section>
      </main>
    </div>
  );
}
